import { safeGetItem, safeSetItem } from './safeLocalStorage';

/**
 * Long-run memory diagnostics.
 *
 * Correlate the same run's main-thread V8 heap, optional user-agent memory,
 * resource counts and separately observed browser process/worker memory.
 * Earlier small heap samples were not synchronized with the large tab:
 * they do not establish a native-memory leak or identify its owner.
 * Socket counts and cumulative canvas allocation are diagnostic signals,
 * not proof of retention or a root cause.
 *
 * Design constraints, because this runs for 16+ hours:
 * - **It must not itself leak.** Sockets are held via `WeakRef` so the monitor
 *   never keeps a dead socket alive, and the snapshot history is a bounded
 *   ring buffer of small plain objects.
 * - **It must survive the tab.** Peak/latest snapshots are mirrored to
 *   localStorage, so the numbers are still recoverable after a 16-hour run
 *   even if the console was cleared or the tab was reloaded.
 * - **It reports through a callback** (same pattern as `requestRateMonitor`)
 *   so this lib stays decoupled from Sentry and is testable without mocks.
 */

/** Chrome-only, non-standard. Present in Chromium; absent elsewhere. */
interface LegacyPerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

/**
 * Optional user-agent memory estimate. It is not a browser-process footprint
 * and is commonly unavailable without cross-origin isolation. Diagnostics
 * must not change COOP/COEP to enable it.
 */
interface UserAgentSpecificMemoryResult {
  bytes: number;
  breakdown?: { bytes: number; types?: string[] }[];
}

interface PerformanceWithMemory extends Performance {
  memory?: LegacyPerformanceMemory;
  measureUserAgentSpecificMemory?: () => Promise<UserAgentSpecificMemoryResult>;
}

/**
 * `WeakRef` is ES2021, but this monorepo compiles against `lib: ['es2020',
 * 'dom']`. Rather than bump the global lib — which would change type
 * resolution for every project in the workspace — the shape is declared
 * locally and the constructor is read off `globalThis`. Runtime support is
 * not a concern (Chrome 84+; this app runs on Chrome 153), and
 * `registerSocket` degrades gracefully if it is ever missing.
 */
interface WeakRefLike<T extends object> {
  deref(): T | undefined;
}

type WeakRefConstructorLike = new <T extends object>(
  target: T,
) => WeakRefLike<T>;

const WeakRefCtor = (
  globalThis as unknown as { WeakRef?: WeakRefConstructorLike }
).WeakRef;

export type SocketCategory = 'olserver' | 'dbserver' | 'replayserver' | 'other';

/**
 * The part of an OpenLayers map the monitor reads, declared structurally so
 * this lib stays free of an `ol` dependency.
 */
export interface DiagnosticMap {
  getTargetElement(): Element | null | undefined;
}

export interface MapCounts {
  /** Registered maps not yet garbage-collected, disposed or not. */
  live: number;
  /**
   * Live maps still bound to a container that is no longer in the page: an
   * offscreen map dropped without `dispose()`. Such a map is never collected,
   * so a rising count is a retained-memory signal, not just an allocation one.
   */
  detached: number;
}

export const MEMORY_QUERY_FAMILIES = [
  'getOverlay',
  'getPeriodEvents',
  'getCurrentEvents',
  'groundings',
  's2sEvents',
  'eventNotificationPoll',
] as const;

export type MemoryQueryFamily = (typeof MEMORY_QUERY_FAMILIES)[number];

export interface MemoryQueryCounts {
  total: number;
  active: number;
  inactive: number;
  fetching: number;
  inactiveFetching: number;
  cachedRecords: number;
}

export interface OverlayMemoryCounts {
  received: number;
  drawn: number;
  pendingDecodes: number;
  ownedCanvases: number;
  ownedCanvasBytes: number;
}

export interface MemoryResources {
  visibilityState: DocumentVisibilityState;
  hasFocus: boolean;
  queries: Record<MemoryQueryFamily, MemoryQueryCounts>;
  overlay: OverlayMemoryCounts;
  toasts: { history: number; active: number; rendered: number };
  /**
   * Canvases currently in the page and their backing stores
   * (`width * height * 4`). Detached canvases can't be seen without retaining
   * them, so offscreen/leaked ones are not included.
   */
  domCanvases: { count: number; bytes: number };
}

/**
 * Timer cadence observed over the run. Chrome clamps background timers
 * (intensive throttling after ~5 min hidden turns sub-minute timers into
 * roughly one wake/minute), so a probe whose gaps far exceed its cadence shows
 * the tab was actually throttled - it distinguishes "hidden but still working"
 * from "the browser treated it as idle". Visibility alone cannot: a window
 * manager that keeps a window reported as visible prevents throttling and the
 * probe stays punctual.
 */
export interface ThrottleProbe {
  /** Configured probe cadence in milliseconds. */
  intervalMs: number;
  /** Probe ticks observed since `start()`. */
  probes: number;
  /** Largest observed gap between consecutive probes. */
  maxGapMs: number;
  /** Probes whose gap far exceeded the cadence (timer throttling). */
  throttledProbes: number;
}

export type MemoryAuthentication = 'pre-auth' | 'authenticated' | 'unknown';

export interface MemorySnapshot {
  runId: string;
  sequence: number;
  /** Loaded asset identifier, not a source commit or application version. */
  assetFingerprint: string;
  authentication: MemoryAuthentication;
  resources?: MemoryResources;
  /** Wall-clock ISO timestamp of the snapshot. */
  timestamp: string;
  /** Milliseconds since `start()` — the x-axis for a 16-hour run. */
  uptimeMs: number;
  /**
   * Sockets still in `readyState === OPEN`, per category. Correlate changes
   * with expected sessions/reconnects; counts alone do not establish a leak.
   */
  openSockets: Record<SocketCategory, number>;
  /** Registered sockets not yet garbage-collected, per category. */
  liveSockets: Record<SocketCategory, number>;
  /** Cumulative sockets ever created, per category. */
  socketsCreated: Record<SocketCategory, number>;
  /** Registered OpenLayers maps, including offscreen snapshot maps. */
  maps: MapCounts;
  /** Cumulative reconnect attempts, per session type. */
  reconnects: Record<string, number>;
  /** Cumulative count of overlay/map canvases allocated. */
  canvasesAllocated: number;
  /** Cumulative estimated allocation (w * h * 4), NOT retained bytes. */
  canvasBytesAllocated: number;
  /** Main-thread V8 heap in MB, not worker heaps or process memory. */
  jsHeapMB: number | null;
  /** Optional user-agent memory estimate in MB, not total process memory. */
  totalMemoryMB: number | null;
  /** Observed timer cadence, to show whether the tab was actually throttled. */
  throttle: ThrottleProbe;
}

export type SnapshotCallback = (snapshot: MemorySnapshot) => void;

export interface MemoryDiagnosticsOptions {
  /** How often to take a snapshot. Default 300 000 ms (five minutes). */
  intervalMs?: number;
  /**
   * How often to sample the timer cadence, in ms. Default 1 000. Kept short
   * so Chrome's background clamping is visible as oversized gaps.
   */
  throttleProbeIntervalMs?: number;
  /** Max snapshots retained in the ring buffer. Default 1 000. */
  maxSnapshots?: number;
  /** Mirror latest/peak snapshots to localStorage. Default true. */
  persist?: boolean;
  assetFingerprint?: string;
  getAuthentication?: () => MemoryAuthentication;
  collectResources?: () => MemoryResources;
}

const DEFAULT_INTERVAL_MS = 300_000;
const DEFAULT_THROTTLE_PROBE_MS = 1_000;
/**
 * A probe gap at least twice its cadence (and never below two seconds) counts
 * as throttled. The floor absorbs ordinary main-thread jitter; Chrome's
 * intensive throttling produces ~60 s gaps, far above it.
 */
const PROBE_THROTTLE_FLOOR_MS = 2_000;
const DEFAULT_MAX_SNAPSHOTS = 1_000;

export const MEMORY_DIAGNOSTICS_LATEST_KEY = 'ais:memoryDiagnostics:latest';
export const MEMORY_DIAGNOSTICS_PEAK_KEY = 'ais:memoryDiagnostics:peak';
export const MEMORY_DIAGNOSTICS_HEAP_PEAK_KEY =
  'ais:memoryDiagnostics:heapPeak';
export const MEMORY_DIAGNOSTICS_ALLOCATION_PEAK_KEY =
  'ais:memoryDiagnostics:allocationPeak';
/** Set this key to '1' in localStorage to enable diagnostics on next load. */
export const MEMORY_DIAGNOSTICS_ENABLED_KEY = 'ais:memoryDiagnostics:enabled';

const CATEGORIES: SocketCategory[] = [
  'olserver',
  'dbserver',
  'replayserver',
  'other',
];

/**
 * Mirrors the URL-sniffing already used by `getSessionTypeFromUrl` in
 * `createSession.ts`, duplicated here so this lib stays dependency-free.
 */
export function categorizeSocketUrl(url: string): SocketCategory {
  if (url.includes('olserver')) return 'olserver';
  if (url.includes('dbserver')) return 'dbserver';
  if (url.includes('replay')) return 'replayserver';
  return 'other';
}

function emptyCounts(): Record<SocketCategory, number> {
  return { olserver: 0, dbserver: 0, replayserver: 0, other: 0 };
}

/**
 * A peak recorded by a previous run, or null when unavailable.
 *
 * In-memory bookkeeping restarts at 0 on every load, so without this the
 * first snapshot after a reload counts as a new peak and overwrites the
 * stored incident - the reload would erase the very evidence the run exists
 * to preserve. Treats anything unparseable as "no record" rather than
 * throwing: a corrupt entry must not take the app down.
 */
function readPersistedPeak(
  key: string,
  field: 'openSockets' | 'jsHeapMB' | 'canvasBytesAllocated',
): number | null {
  const raw = safeGetItem(key);
  if (!raw) return null;

  try {
    const peak = JSON.parse(raw) as Partial<MemorySnapshot>;
    const recorded =
      field === 'openSockets' ? peak?.openSockets?.olserver : peak?.[field];
    return typeof recorded === 'number' &&
      Number.isFinite(recorded) &&
      recorded >= 0
      ? recorded
      : null;
  } catch {
    return null;
  }
}

function createRunId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `run-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  );
}

function emptyOverlayCounts(): OverlayMemoryCounts {
  return {
    received: 0,
    drawn: 0,
    pendingDecodes: 0,
    ownedCanvases: 0,
    ownedCanvasBytes: 0,
  };
}

interface TrackedSocket {
  ref: WeakRefLike<WebSocket>;
  category: SocketCategory;
}

class MemoryDiagnosticsImpl {
  private sockets: TrackedSocket[] = [];
  private maps: WeakRefLike<DiagnosticMap>[] = [];
  private socketsCreated = emptyCounts();
  private reconnects: Record<string, number> = {};
  private canvasesAllocated = 0;
  private canvasBytesAllocated = 0;

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private probeIntervalId: ReturnType<typeof setInterval> | null = null;
  private throttleProbeMs = DEFAULT_THROTTLE_PROBE_MS;
  private probeCount = 0;
  private maxProbeGapMs = 0;
  private throttledProbes = 0;
  private lastProbeAt = 0;
  private callback: SnapshotCallback | null = null;
  private startedAt = 0;
  private persist = true;
  private maxSnapshots = DEFAULT_MAX_SNAPSHOTS;
  private snapshots: MemorySnapshot[] = [];
  private peakOpenOlSockets = 0;
  private peakHeapMB: number | null = null;
  private peakCanvasBytesAllocated = 0;
  private runId = createRunId();
  private sequence = 0;
  private generation = 0;
  private pendingCapture: Promise<MemorySnapshot> | null = null;
  private assetFingerprint = 'unknown:module';
  private getAuthentication: (() => MemoryAuthentication) | undefined;
  private collectResources: (() => MemoryResources) | undefined;
  private memoryWarningShown = false;
  private intervalErrorShown = false;
  private overlay = emptyOverlayCounts();

  /** True when `start()` has been called and the interval is running. */
  get isRunning(): boolean {
    return this.intervalId !== null;
  }

  /**
   * Whether diagnostics should run. Opt-in via localStorage so a tester can
   * enable it on a deployed environment without a rebuild.
   */
  static isEnabled(): boolean {
    return safeGetItem(MEMORY_DIAGNOSTICS_ENABLED_KEY) === '1';
  }

  start(onSnapshot?: SnapshotCallback, options?: MemoryDiagnosticsOptions) {
    // Re-starting must not leave the previous interval running.
    this.stop();

    this.callback = onSnapshot ?? null;
    this.startedAt = Date.now();
    this.runId = createRunId();
    this.sequence = 0;
    this.assetFingerprint = options?.assetFingerprint ?? 'unknown:module';
    this.getAuthentication = options?.getAuthentication;
    this.collectResources = options?.collectResources;
    this.memoryWarningShown = false;
    this.intervalErrorShown = false;
    this.persist = options?.persist ?? true;
    const requestedMax = options?.maxSnapshots ?? DEFAULT_MAX_SNAPSHOTS;
    this.maxSnapshots = Number.isFinite(requestedMax)
      ? Math.max(1, Math.floor(requestedMax))
      : DEFAULT_MAX_SNAPSHOTS;

    // Carry forward a peak recorded before a reload, so a long incident is
    // not reset to the count of whatever this fresh tab happens to open.
    // Only when persisting: `persist: false` means "leave localStorage
    // alone", read included.
    this.peakOpenOlSockets = this.persist
      ? (readPersistedPeak(MEMORY_DIAGNOSTICS_PEAK_KEY, 'openSockets') ?? 0)
      : 0;
    this.peakHeapMB = this.persist
      ? readPersistedPeak(MEMORY_DIAGNOSTICS_HEAP_PEAK_KEY, 'jsHeapMB')
      : null;
    this.peakCanvasBytesAllocated = this.persist
      ? (readPersistedPeak(
          MEMORY_DIAGNOSTICS_ALLOCATION_PEAK_KEY,
          'canvasBytesAllocated',
        ) ?? 0)
      : 0;

    const intervalMs = options?.intervalMs ?? DEFAULT_INTERVAL_MS;
    this.intervalId = setInterval(() => {
      // Skip rather than attach another promise handler to a stalled browser
      // measurement on every tick.
      if (this.pendingCapture) return;
      void this.capture().catch(() => {
        if (this.intervalErrorShown) return;
        this.intervalErrorShown = true;
        console.error(
          '[memoryDiagnostics] snapshot or reporting failed; later samples will retry.',
        );
      });
    }, intervalMs);

    // The probe is deliberately much shorter than the snapshot interval: a
    // five-minute timer is longer than Chrome's one-minute background clamp, so
    // it would stay punctual even while throttled and reveal nothing. At one
    // second, any clamping shows up as an oversized gap.
    this.throttleProbeMs =
      options?.throttleProbeIntervalMs ?? DEFAULT_THROTTLE_PROBE_MS;
    this.probeCount = 0;
    this.maxProbeGapMs = 0;
    this.throttledProbes = 0;
    this.lastProbeAt = Date.now();
    this.probeIntervalId = setInterval(
      () => this.sampleProbe(),
      this.throttleProbeMs,
    );
  }

  stop() {
    this.generation++;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.probeIntervalId) {
      clearInterval(this.probeIntervalId);
      this.probeIntervalId = null;
    }
    this.callback = null;
    this.collectResources = undefined;
    this.getAuthentication = undefined;
  }

  /** Clears all counters and history. Primarily for tests. */
  reset() {
    this.stop();
    this.sockets = [];
    this.maps = [];
    this.socketsCreated = emptyCounts();
    this.reconnects = {};
    this.canvasesAllocated = 0;
    this.canvasBytesAllocated = 0;
    this.snapshots = [];
    this.peakOpenOlSockets = 0;
    this.startedAt = 0;
    this.peakHeapMB = null;
    this.peakCanvasBytesAllocated = 0;
    this.runId = createRunId();
    this.sequence = 0;
    this.pendingCapture = null;
    this.assetFingerprint = 'unknown:module';
    this.overlay = emptyOverlayCounts();
    this.throttleProbeMs = DEFAULT_THROTTLE_PROBE_MS;
    this.probeCount = 0;
    this.maxProbeGapMs = 0;
    this.throttledProbes = 0;
    this.lastProbeAt = 0;
  }

  /**
   * Record the gap since the previous probe. No work beyond arithmetic: this
   * runs every second for the life of the tab.
   */
  private sampleProbe() {
    const now = Date.now();
    const gap = now - this.lastProbeAt;
    this.lastProbeAt = now;
    this.probeCount += 1;
    if (gap > this.maxProbeGapMs) this.maxProbeGapMs = gap;
    if (gap >= Math.max(this.throttleProbeMs * 2, PROBE_THROTTLE_FLOOR_MS)) {
      this.throttledProbes += 1;
    }
  }

  /**
   * Register a newly created WebSocket. Held via `WeakRef` so registration
   * never prevents collection — if a socket disappears from this list it is
   * because the browser reclaimed it, which is exactly the signal we want.
   */
  registerSocket(socket: WebSocket) {
    // Guard: WeakRef is unavailable in some older test/jsdom environments.
    if (!WeakRefCtor) return;

    const category = categorizeSocketUrl(socket.url ?? '');
    this.socketsCreated[category] += 1;
    this.sockets.push({ ref: new WeakRefCtor(socket), category });
    this.pruneCollectedSockets();
  }

  /** Record a reconnect attempt for a session type. */
  trackReconnect(sessionType: string) {
    this.reconnects[sessionType] = (this.reconnects[sessionType] ?? 0) + 1;
  }

  /**
   * Register a newly created OpenLayers map, including offscreen ones built
   * only for a snapshot. Held via `WeakRef`, like sockets, so registering a
   * map never keeps it alive. Every `new Map(...)` site should call this.
   */
  registerMap(map: DiagnosticMap) {
    if (!WeakRefCtor) return;

    this.maps.push(new WeakRefCtor(map));
    this.pruneCollectedMaps();
  }

  /**
   * Record an allocation estimate from canvas backing dimensions, not CSS
   * pixels. Four bytes per pixel does not measure native retention.
   */
  trackCanvas(width: number, height: number) {
    if (!Number.isFinite(width) || !Number.isFinite(height)) return;
    this.canvasesAllocated += 1;
    this.canvasBytesAllocated += Math.max(0, width * height * 4);
  }

  getOverlayCounts(): OverlayMemoryCounts {
    return { ...this.overlay };
  }

  /**
   * One scalar owner per mounted overlay. Completion closures hold counters
   * only: no image/canvas references and no per-frame registry.
   */
  createOverlayOwner() {
    const counts = this.overlay;
    let disposed = false;
    let pending = 0;
    let ownedBytes: number | null = null;
    return {
      receive: () => {
        if (!disposed && this.isRunning) counts.received++;
      },
      decode: () => {
        let outstanding = !disposed && this.isRunning;
        if (outstanding) {
          pending++;
          counts.pendingDecodes++;
        }
        return () => {
          if (!outstanding || disposed) return;
          outstanding = false;
          pending--;
          counts.pendingDecodes--;
        };
      },
      drawn: (width: number, height: number) => {
        if (disposed || !this.isRunning) return;
        const bytes = Math.max(0, width * height * 4);
        if (!Number.isFinite(bytes)) return;
        counts.drawn++;
        if (ownedBytes === null) counts.ownedCanvases++;
        counts.ownedCanvasBytes += bytes - (ownedBytes ?? 0);
        ownedBytes = bytes;
      },
      dispose: () => {
        if (disposed) return;
        disposed = true;
        counts.pendingDecodes -= pending;
        if (ownedBytes !== null) {
          counts.ownedCanvases--;
          counts.ownedCanvasBytes -= ownedBytes;
        }
      },
    };
  }

  /** Drop refs whose sockets have been garbage-collected. */
  private pruneCollectedSockets() {
    this.sockets = this.sockets.filter(
      (tracked) => tracked.ref.deref() !== undefined,
    );
  }

  private countSockets() {
    const open = emptyCounts();
    const live = emptyCounts();

    for (const tracked of this.sockets) {
      const socket = tracked.ref.deref();
      if (!socket) continue;
      live[tracked.category] += 1;
      if (socket.readyState === WebSocket.OPEN) {
        open[tracked.category] += 1;
      }
    }

    return { open, live };
  }

  private pruneCollectedMaps() {
    this.maps = this.maps.filter((ref) => ref.deref() !== undefined);
  }

  private countMaps(): MapCounts {
    const counts: MapCounts = { live: 0, detached: 0 };
    for (const ref of this.maps) {
      const map = ref.deref();
      if (!map) continue;
      counts.live += 1;
      const target = map.getTargetElement();
      if (target && !target.isConnected) counts.detached += 1;
    }
    return counts;
  }

  private async readTotalMemoryMB(): Promise<number | null> {
    const perf = globalThis.performance as PerformanceWithMemory | undefined;
    const measure = perf?.measureUserAgentSpecificMemory;
    if (!measure) return null;

    try {
      const result = await measure.call(perf);
      return Number.isFinite(result.bytes) && result.bytes >= 0
        ? Math.round(result.bytes / 1048576)
        : null;
    } catch {
      if (!this.memoryWarningShown) {
        this.memoryWarningShown = true;
        console.warn(
          '[memoryDiagnostics] optional user-agent memory unavailable; recorded as null.',
        );
      }
      return null;
    }
  }

  private readJsHeapMB(): number | null {
    const perf = globalThis.performance as PerformanceWithMemory | undefined;
    const used = perf?.memory?.usedJSHeapSize;
    return typeof used === 'number' && Number.isFinite(used) && used >= 0
      ? Math.round(used / 1048576)
      : null;
  }

  /** Take a snapshot now. Exposed so tests and manual triage can force one. */
  capture(): Promise<MemorySnapshot> {
    if (this.pendingCapture) return this.pendingCapture;
    const pending = this.captureSnapshot(this.generation).finally(() => {
      if (this.pendingCapture === pending) this.pendingCapture = null;
    });
    this.pendingCapture = pending;
    return pending;
  }

  private async captureSnapshot(generation: number): Promise<MemorySnapshot> {
    this.pruneCollectedSockets();
    const { open, live } = this.countSockets();
    this.pruneCollectedMaps();
    const maps = this.countMaps();

    const snapshot: MemorySnapshot = {
      runId: this.runId,
      sequence: ++this.sequence,
      assetFingerprint: this.assetFingerprint,
      authentication: this.getAuthentication?.() ?? 'unknown',
      ...(this.collectResources && { resources: this.collectResources() }),
      timestamp: new Date().toISOString(),
      uptimeMs: this.startedAt ? Date.now() - this.startedAt : 0,
      openSockets: open,
      liveSockets: live,
      socketsCreated: { ...this.socketsCreated },
      maps,
      reconnects: { ...this.reconnects },
      canvasesAllocated: this.canvasesAllocated,
      canvasBytesAllocated: this.canvasBytesAllocated,
      jsHeapMB: this.readJsHeapMB(),
      totalMemoryMB: await this.readTotalMemoryMB(),
      throttle: {
        intervalMs: this.throttleProbeMs,
        probes: this.probeCount,
        maxGapMs: this.maxProbeGapMs,
        throttledProbes: this.throttledProbes,
      },
    };

    // Optional browser measurement can outlive stop/restart. It remains
    // single-flight, but its obsolete result must not publish into a new run.
    if (generation !== this.generation) return snapshot;

    this.snapshots.push(snapshot);
    if (this.snapshots.length > this.maxSnapshots) {
      this.snapshots.splice(0, this.snapshots.length - this.maxSnapshots);
    }

    // Peaks are independent signals; `persist` only controls storage, not
    // in-memory bookkeeping.
    const isNewPeak = open.olserver > this.peakOpenOlSockets;
    if (isNewPeak) {
      this.peakOpenOlSockets = open.olserver;
    }
    const isHeapPeak =
      snapshot.jsHeapMB !== null &&
      (this.peakHeapMB === null || snapshot.jsHeapMB > this.peakHeapMB);
    const isAllocationPeak =
      snapshot.canvasBytesAllocated > this.peakCanvasBytesAllocated;
    if (isHeapPeak) this.peakHeapMB = snapshot.jsHeapMB;
    if (isAllocationPeak)
      this.peakCanvasBytesAllocated = snapshot.canvasBytesAllocated;

    if (this.persist) {
      safeSetItem(MEMORY_DIAGNOSTICS_LATEST_KEY, JSON.stringify(snapshot));
      if (isNewPeak) {
        safeSetItem(MEMORY_DIAGNOSTICS_PEAK_KEY, JSON.stringify(snapshot));
      }
      if (isHeapPeak)
        safeSetItem(MEMORY_DIAGNOSTICS_HEAP_PEAK_KEY, JSON.stringify(snapshot));
      if (isAllocationPeak)
        safeSetItem(
          MEMORY_DIAGNOSTICS_ALLOCATION_PEAK_KEY,
          JSON.stringify(snapshot),
        );
    }

    this.callback?.(snapshot);

    return snapshot;
  }

  /** All retained snapshots, oldest first. */
  getSnapshots(): MemorySnapshot[] {
    return [...this.snapshots];
  }

  /**
   * Human-readable summary for pasting into a ticket after a long run.
   */
  getReport(): string {
    const latest = this.snapshots[this.snapshots.length - 1];
    if (!latest) return 'memoryDiagnostics: no snapshots captured yet.';

    const hours = (latest.uptimeMs / 3_600_000).toFixed(1);
    const canvasGB = (latest.canvasBytesAllocated / 1073741824).toFixed(2);
    const openList = CATEGORIES.map(
      (category) => `${category}=${latest.openSockets[category]}`,
    ).join(' ');
    const createdList = CATEGORIES.map(
      (category) => `${category}=${latest.socketsCreated[category]}`,
    ).join(' ');

    return [
      `memoryDiagnostics after ${hours}h:`,
      `  run: ${latest.runId} sample=${latest.sequence} ${latest.authentication} artifact=${latest.assetFingerprint}`,
      `  open sockets:    ${openList}`,
      `  sockets created: ${createdList}`,
      `  reconnects:      ${JSON.stringify(this.reconnects)}`,
      `  OpenLayers maps: live=${latest.maps.live} detached=${latest.maps.detached} (detached = bound to a container no longer in the page)`,
      `  canvases:        ${latest.canvasesAllocated} (${canvasGB} GB cumulative allocation, not retained)`,
      `  main-thread V8 heap: ${latest.jsHeapMB ?? 'n/a'} MB`,
      `  user-agent memory estimate: ${latest.totalMemoryMB ?? 'n/a'} MB (not process footprint)`,
      `  throttle probe: ${latest.throttle.probes} ticks @ ${latest.throttle.intervalMs}ms, max gap ${latest.throttle.maxGapMs}ms, ${latest.throttle.throttledProbes} throttled (oversized gaps = the browser clamped the tab)`,
      `  peak open olserver sockets: ${this.peakOpenOlSockets} (legacy persisted peak)`,
      `  peak main-thread V8 heap: ${this.peakHeapMB ?? 'n/a'} MB`,
      `  peak cumulative canvas allocation: ${this.peakCanvasBytesAllocated} bytes`,
      ...(latest.resources
        ? [`  resources: ${JSON.stringify(latest.resources)}`]
        : []),
    ].join('\n');
  }
}

export const memoryDiagnostics = new MemoryDiagnosticsImpl();

/** Whether diagnostics are opted in via localStorage. */
export const isMemoryDiagnosticsEnabled = () =>
  MemoryDiagnosticsImpl.isEnabled();
